const React = require('react');
const { useTranslation } = require('react-i18next');
const styles = require('./styles.less');

const owl = require('/assets/images/caroline-owl.png');
const hero = require('/assets/images/caroline-landing-hero.png');
const preview = require('/assets/images/caroline-landing-preview.png');

const services = [
    { name: 'Netflix', logo: require('/assets/images/landing-services/netflix.svg') },
    { name: 'Prime Video', logo: require('/assets/images/landing-services/amazonprime.svg') },
    { name: 'Apple TV', logo: require('/assets/images/landing-services/appletv.svg') },
    { name: 'HBO', logo: require('/assets/images/landing-services/hbo.svg') },
    { name: 'Paramount+', logo: require('/assets/images/landing-services/paramountplus.svg') }
];

const StreamingMoment = () => {
    const { t } = useTranslation();
    const sectionRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    React.useEffect(() => {
        if (!window.IntersectionObserver) {
            setPlaying(true);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setPlaying(true);
                observer.disconnect();
            }
        }, { threshold: 0.25 });
        observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);
    return (
        <section ref={sectionRef} className={styles.streamingMoment} aria-labelledby="streaming-title">
            <div className={styles.noAds}>
                <svg className={styles.zero} viewBox="0 0 80 110" aria-hidden="true"><ellipse cx="40" cy="55" rx="31" ry="46" fill="none" stroke="currentColor" strokeWidth="9" /></svg>
                <div>
                    <h2>{t('CAROLINE_LANDING_ZERO_ADS', { defaultValue: 'Zero anúncios.' })}</h2>
                    <p>{t('CAROLINE_LANDING_NO_INTERRUPTION', { defaultValue: 'Na Caroline, a interface é tua. Sem anúncios a interromper a descoberta.' })}</p>
                </div>
            </div>
            <div className={styles.streamingStory}>
                <p className={styles.storyIntro}>{t('CAROLINE_LANDING_MANY_SERVICES', { defaultValue: 'Tantas aplicações. Tantas escolhas.' })}</p>
                <div className={styles.convergence} data-playing={playing}>
                    <div className={styles.serviceMarks} aria-label="Marcas de streaming: Netflix, Prime Video, Apple TV, HBO e Paramount+">
                        {services.map(({ name, logo }, index) => (
                            <div key={name} className={styles.serviceMark} style={{ '--slot': index }}>
                                <img src={logo} alt={name} width="110" height="48" loading="lazy" />
                            </div>
                        ))}
                    </div>
                    <div className={styles.owlDestination}>
                        <img src={owl} alt="" width="128" height="128" loading="lazy" />
                        <span>{t('CAROLINE_LANDING_CAROLINE', { defaultValue: 'Caroline' })}</span>
                    </div>
                </div>
                <h2 id="streaming-title">{t('CAROLINE_LANDING_ONE_SERVICE', { defaultValue: 'Só precisas de um serviço: Caroline.' })}</h2>
                <p className={styles.storyDescription}>{t('CAROLINE_LANDING_ONE_PLACE', { defaultValue: 'Um lugar para descobrir, escolher e guardar a tua próxima história.' })}</p>
                <div className={styles.storyActions}>
                    <a className={styles.primaryButton} href="/#/">{t('CAROLINE_LANDING_OPEN', { defaultValue: 'Conhecer a Caroline' })}</a>
                </div>
                <p className={styles.brandNote}>{t('CAROLINE_LANDING_BRAND_NOTE', { defaultValue: 'Marcas apresentadas como referência, sem afiliação. A disponibilidade dos títulos depende das fontes.' })}</p>
            </div>
        </section>
    );
};

const Landing = () => {
    const { t } = useTranslation();
    const pageRef = React.useRef(null);
    const scrollTo = (id) => {
        const target = pageRef.current?.querySelector(`#${id}`);
        target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        target?.focus({ preventScroll: true });
    };

    return (
        <div className={styles.page} ref={pageRef} lang="pt">
            <button className={styles.skipLink} onClick={() => scrollTo('landing-main')}>{t('CAROLINE_LANDING_SALTAR_PARA_O_CONTEUDO', {
                defaultValue: 'Saltar para o conteúdo'
            })}</button>
            <header className={styles.header}>
                <a className={styles.brand} href="/" aria-label="Caroline — página inicial">
                    <img src={owl} alt="" width="42" height="42" />
                    <span>{t('CAROLINE_LANDING_CAROLINE', {
                        defaultValue: 'Caroline'
                    })}</span>
                </a>
                <nav className={styles.navigation} aria-label="Sobre a Caroline">
                    <button onClick={() => scrollTo('experiencia')}>{t('CAROLINE_LANDING_A_EXPERIENCIA', {
                        defaultValue: 'A experiência'
                    })}</button>
                    <button onClick={() => scrollTo('perguntas')}>{t('CAROLINE_LANDING_PERGUNTAS', {
                        defaultValue: 'Perguntas'
                    })}</button>
                </nav>
                <a className={styles.login} href="/#/caroline-auth">{t('CAROLINE_LANDING_INICIAR_SESSAO', {
                    defaultValue: 'Iniciar sessão'
                })}</a>
            </header>
            <main id="landing-main" tabIndex="-1">
                <section className={styles.hero} aria-labelledby="landing-title">
                    <img className={styles.heroImage} src={hero} alt="" loading="eager" width="1792" height="1024" />
                    <div className={styles.heroShade} />
                    <div className={styles.heroContent}>
                        <h1 id="landing-title">{t('CAROLINE_LANDING_DA_ESPACO_AS', {
                            defaultValue: 'Dá espaço às'
                        })}<br />{t('CAROLINE_LANDING_BOAS_HISTORIAS', {
                            defaultValue: 'boas histórias.'
                        })}</h1>
                        <p>{t('CAROLINE_LANDING_FILMES_SERIES_E_OS_TITULOS_QUE_QUERES_GUARDAR', {
                            defaultValue: 'Filmes, séries e os títulos que queres guardar.'
                        })}<br className={styles.desktopBreak} />{t('CAROLINE_LANDING_UM_LUGAR_TRANQUILO_PARA_ESCOLHER_O_QUE_VER', {
                            defaultValue: ' Um lugar tranquilo para escolher o que ver.'
                        })}</p>
                        <div className={styles.heroActions}>
                            <a className={styles.primaryButton} href="/#/">
                                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>{t('CAROLINE_LANDING_ENTRAR_NA_CAROLINE', {
                                    defaultValue: 'Entrar na Caroline'
                                })}</a>
                            <button className={styles.textButton} onClick={() => scrollTo('experiencia')}>{t('CAROLINE_LANDING_CONHECER_O_PROJECTO', {
                                defaultValue: 'Conhecer o projecto'
                            })}</button>
                        </div>
                        <span className={styles.heroNote}>{t('CAROLINE_LANDING_ABRE_NO_NAVEGADOR_ENCONTRA_O_TEU_PROXIMO_FILME', {
                            defaultValue: 'Abre no navegador. Encontra o teu próximo filme.'
                        })}</span>
                    </div>
                    <div className={styles.heroBottom}>
                        <span>{t('CAROLINE_LANDING_MENOS_RUIDO_MAIS_CINEMA', {
                            defaultValue: 'Menos ruído. Mais cinema.'
                        })}</span>
                        <button onClick={() => scrollTo('experiencia')} aria-label="Descobrir a experiência Caroline">
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 4v15m-6-6 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
                        </button>
                    </div>
                </section>

                <StreamingMoment />

                <section className={styles.experience} id="experiencia" tabIndex="-1" aria-labelledby="experience-title">
                    <div className={styles.sectionHeading}>
                        <h2 id="experience-title">{t('CAROLINE_LANDING_O_CONTEUDO_EM_PRIMEIRO_PLANO', {
                            defaultValue: 'O conteúdo em primeiro plano.'
                        })}</h2>
                        <p>{t(
                            'CAROLINE_LANDING_UMA_INTERFACE_QUE_DA_ESPACO_AO_QUE_IMPORTA_DESCOBRIR_ESCOLHER_E_V',
                            {
                                defaultValue: 'Uma interface que dá espaço ao que importa: descobrir, escolher e voltar às histórias de que gostas.'
                            }
                        )}</p>
                    </div>
                    <figure className={styles.productFrame}>
                        <img src={preview} alt="A página inicial real da Caroline, com destaque de um filme e fileiras de títulos para explorar." width="1440" height="900" loading="lazy" decoding="async" />
                        <figcaption>{t(
                            'CAROLINE_LANDING_UM_OLHAR_SOBRE_A_CAROLINE_O_CATALOGO_PODE_VARIAR_CONFORME_AS_FONT',
                            {
                                defaultValue: 'Um olhar sobre a Caroline. O catálogo pode variar conforme as fontes disponíveis.'
                            }
                        )}</figcaption>
                    </figure>
                    <div className={styles.features}>
                        <article>
                            <h3>{t('CAROLINE_LANDING_SEGUE_A_CURIOSIDADE', {
                                defaultValue: 'Segue a curiosidade.'
                            })}</h3>
                            <p>{t(
                                'CAROLINE_LANDING_EXPLORA_FILMES_E_SERIES_PESQUISA_UM_TITULO_E_LE_OS_DETALHES_ANTES',
                                {
                                    defaultValue: 'Explora filmes e séries, pesquisa um título e lê os detalhes antes de escolher.'
                                }
                            )}</p>
                            <a href="/#/discover">{t('CAROLINE_LANDING_EXPLORAR_O_CATALOGO', {
                                defaultValue: 'Explorar o catálogo'
                            })}</a>
                        </article>
                        <article>
                            <h3>{t('CAROLINE_LANDING_FICA_PARA_DEPOIS', {
                                defaultValue: 'Fica para depois.'
                            })}</h3>
                            <p>{t(
                                'CAROLINE_LANDING_GUARDA_OS_TITULOS_QUE_CHAMARAM_A_TUA_ATENCAO_A_TUA_BIBLIOTECA_REU',
                                {
                                    defaultValue: 'Guarda os títulos que chamaram a tua atenção. A tua biblioteca reúne o que queres voltar a encontrar.'
                                }
                            )}</p>
                            <a href="/#/library">{t('CAROLINE_LANDING_ABRIR_A_BIBLIOTECA', {
                                defaultValue: 'Abrir a biblioteca'
                            })}</a>
                        </article>
                        <article>
                            <h3>{t('CAROLINE_LANDING_A_TUA_MANEIRA', {
                                defaultValue: 'À tua maneira.'
                            })}</h3>
                            <p>{t(
                                'CAROLINE_LANDING_AJUSTA_O_IDIOMA_AS_LEGENDAS_E_AS_PREFERENCIAS_DE_REPRODUCAO_NUMA',
                                {
                                    defaultValue: 'Ajusta o idioma, as legendas e as preferências de reprodução numa interface simples.'
                                }
                            )}</p>
                            <a href="/#/settings">{t('CAROLINE_LANDING_CONHECER_AS_DEFINICOES', {
                                defaultValue: 'Conhecer as definições'
                            })}</a>
                        </article>
                    </div>
                </section>

                <section className={styles.questions} id="perguntas" tabIndex="-1" aria-labelledby="questions-title">
                    <div>
                        <h2 id="questions-title">{t('CAROLINE_LANDING_ANTES_DE_ENTRAR', {
                            defaultValue: 'Antes de entrar.'
                        })}</h2>
                        <p>{t('CAROLINE_LANDING_O_ESSENCIAL_SOBRE_A_CAROLINE', {
                            defaultValue: 'O essencial sobre a Caroline.'
                        })}</p>
                    </div>
                    <div className={styles.answers}>
                        <details>
                            <summary>{t('CAROLINE_LANDING_O_QUE_E_A_CAROLINE', {
                                defaultValue: 'O que é a Caroline?'
                            })}</summary>
                            <p>{t(
                                'CAROLINE_LANDING_E_UMA_APLICACAO_WEB_PARA_DESCOBRIR_FILMES_E_SERIES_ORGANIZAR_TITU',
                                {
                                    defaultValue: 'É uma aplicação web para descobrir filmes e séries, organizar títulos na tua biblioteca e reproduzir fontes de vídeo compatíveis.'
                                }
                            )}</p>
                        </details>
                        <details>
                            <summary>{t('CAROLINE_LANDING_PRECISO_DE_INSTALAR_ALGUMA_COISA', {
                                defaultValue: 'Preciso de instalar alguma coisa?'
                            })}</summary>
                            <p>{t(
                                'CAROLINE_LANDING_PODES_ABRIR_A_INTERFACE_DIRECTAMENTE_NO_NAVEGADOR_A_REPRODUCAO_DE',
                                {
                                    defaultValue: 'Podes abrir a interface directamente no navegador. A reprodução depende da fonte escolhida, do formato do vídeo e da compatibilidade do teu dispositivo.'
                                }
                            )}</p>
                        </details>
                        <details>
                            <summary>{t('CAROLINE_LANDING_PARA_QUE_SERVE_A_CONTA_CAROLINE', {
                                defaultValue: 'Para que serve a conta Caroline?'
                            })}</summary>
                            <p>{t(
                                'CAROLINE_LANDING_A_CONTA_PERMITE_GUARDAR_A_TUA_BIBLIOTECA_CAROLINE_ENTRE_SESSOES_P',
                                {
                                    defaultValue: 'A conta permite guardar a tua biblioteca Caroline entre sessões. Podes entrar na aplicação e explorar antes de iniciar sessão.'
                                }
                            )}</p>
                        </details>
                        <details>
                            <summary>{t('CAROLINE_LANDING_TODOS_OS_TITULOS_ESTAO_DISPONIVEIS_PARA_VER', {
                                defaultValue: 'Todos os títulos estão disponíveis para ver?'
                            })}</summary>
                            <p>{t(
                                'CAROLINE_LANDING_O_CATALOGO_APRESENTA_INFORMACAO_SOBRE_TITULOS_A_DISPONIBILIDADE_D',
                                {
                                    defaultValue: 'O catálogo apresenta informação sobre títulos. A disponibilidade de fontes de reprodução pode variar. Utiliza apenas conteúdos a que tenhas direito de acesso.'
                                }
                            )}</p>
                        </details>
                    </div>
                </section>

                <section className={styles.invitation} aria-labelledby="invitation-title">
                    <img src={owl} alt="" width="76" height="76" loading="lazy" />
                    <h2 id="invitation-title">{t('CAROLINE_LANDING_A_NOITE_E_TUA', {
                        defaultValue: 'A noite é tua.'
                    })}<br />{t('CAROLINE_LANDING_ESCOLHE_A_HISTORIA', {
                        defaultValue: 'Escolhe a história.'
                    })}</h2>
                    <a className={styles.primaryButton} href="/#/">{t('CAROLINE_LANDING_ABRIR_A_CAROLINE', {
                        defaultValue: 'Abrir a Caroline'
                    })}</a>
                </section>
            </main>
            <footer className={styles.footer}>
                <span className={styles.footerBrand}>{t('CAROLINE_LANDING_CAROLINE', {
                    defaultValue: 'Caroline'
                })}</span>
                <span>{t('CAROLINE_LANDING_FEITA_PARA_BOAS_HISTORIAS', {
                    defaultValue: 'Feita para boas histórias.'
                })}</span>
                <a href="/#/">{t('CAROLINE_LANDING_ENTRAR_NA_APLICACAO', {
                    defaultValue: 'Entrar na aplicação'
                })}</a>
            </footer>
        </div>
    );
};

module.exports = Landing;
